import React from "react";
import { Box, Typography, Paper } from "@mui/material";
import { useTranslation } from "react-i18next";
import { DEFAULT_TIER_COLORS, FALLBACK_TIER_COLOR, TIER_COLORS, TIER_LABEL_INK } from "../../Styles/theme";

type TierItem = {
  Id: number;
  AlbumName?: string;
  SongName?: string;
  Artist?: string;
  ArtistId?: number;
  YearReleased?: number;
  AlbumId?: number;
  ProfilePicture?: string;
  IsCustom?: boolean;
  CustomImage?: string;
};

type ReadOnlyTierListProps = {
  title: string;
  tierNames: string[];
  tiers: Record<string, TierItem[]>;
  customTiers?: { name: string; color: string }[] | null;
  listType: string;
};

const getAlbumFolder = (albumId: number): string | undefined => {
  for (let x = 500; x < 10000; x += 500) {
    if (albumId <= x) {
      return `${x - 499}_${x}`;
    }
  }
  return undefined;
};

const getAlbumArt = (albumId: number): string => {
  const folderPath = getAlbumFolder(albumId);
  if (folderPath) {
    return `https://musictierlist.com/AlbumImages/${folderPath}/Album_${albumId}.jpg`;
  }
  return `https://musictierlist.com/AlbumImages/Album_${albumId}.jpg`;
};

const getProfilePicture = (artist: string, path?: string, image?: string): string => {
  if (image && !image.includes("https")) {
    if (artist.includes("Kanye") && artist.includes("Ty")) {
      return "https://musicrandomizer.com/Images/ProfilePictures/K/Kanye+Ty.jpg";
    }
    if (/[a-zA-Z]/.test(artist[0])) {
      return `https://musicrandomizer.com/Images/ProfilePictures/${artist[0].toUpperCase()}/${path}.jpg`;
    }
    return `https://musicrandomizer.com/Images/ProfilePictures/${path}.jpg`;
  }
  return image || "/Images/DefaultAlbumCover.svg";
};

const getItemCover = (item: TierItem, listType: string): string => {
  if (item.IsCustom) {
    return item.CustomImage || "/Images/DefaultAlbumCover.svg";
  }
  if (listType === "Artists") {
    return getProfilePicture(item.Artist || "", item.ProfilePicture, item.ProfilePicture);
  }
  if (listType === "Songs" && item.AlbumId) {
    return getAlbumArt(item.AlbumId);
  }
  return getAlbumArt(item.Id);
};

const ReadOnlyTierList: React.FC<ReadOnlyTierListProps> = ({
  title, tierNames, tiers, customTiers, listType,
}) => {
  const { t } = useTranslation();
  const colorMap: Record<string, string> = {};
  if (customTiers && Array.isArray(customTiers)) {
    customTiers.forEach(t => { if (t.name && t.color) colorMap[t.name] = t.color; });
  }
  const getColor = (name: string, index: number) =>
    colorMap[name] || TIER_COLORS[name] || DEFAULT_TIER_COLORS[index % DEFAULT_TIER_COLORS.length] || FALLBACK_TIER_COLOR;

  return (
    <Box sx={{ padding: 3, maxWidth: 1200, margin: "0 auto" }}>
      {title && (
        <Typography variant="h5" sx={{ fontWeight: "bold", textAlign: "center", mb: 2, color: "var(--text-primary)" }}>
          {title}
        </Typography>
      )}
      {tierNames.map((tierName, index) => {
        const tierColor = getColor(tierName, index);
        const items = tiers[tierName] || [];
        return (
          <Box
            key={tierName}
            sx={{
              display: "flex",
              gap: "20px",
              bgcolor: "var(--surface-1)",
              borderRadius: "8px",
              border: "1px solid var(--border-soft)",
              borderLeft: `5px solid ${tierColor}`,
              mb: "4px",
              p: 2,
            }}
          >
            <Box sx={{
              minHeight: "75px", minWidth: "80px",
              backgroundColor: tierColor, borderRadius: "4px",
              display: "flex", alignItems: "center", justifyContent: "center",
              padding: "0 8px",
            }}>
              <Typography variant="h6" sx={{ color: TIER_LABEL_INK, fontWeight: 700 }}>
                {tierName}
              </Typography>
            </Box>
            <Box sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(100px, 1fr))",
              gap: 1, width: "100%",
            }}>
              {items.map((item) => (
                <Paper key={item.Id} sx={{
                  width: "100%", display: "flex", flexDirection: "column",
                  justifyContent: "center", alignItems: "center",
                  minHeight: "150px", maxHeight: "150px",
                  overflow: "hidden", backgroundColor: "var(--surface-1)",
                  borderRadius: "6px", border: "1px solid var(--border-soft)",
                }}>
                  <img
                    src={getItemCover(item, listType)}
                    alt={item.SongName || item.AlbumName || item.Artist}
                    width={100} height={100}
                    style={{ objectFit: "cover" }}
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = "/Images/DefaultAlbumCover.svg";
                    }}
                  />
                  {(item.SongName || item.AlbumName) && (
                    <Typography
                      variant="body2"
                      sx={{
                        color: "var(--text-primary)", fontSize: "12px", mt: "4px",
                        whiteSpace: "pre-line", textAlign: "center",
                        overflow: "hidden", textOverflow: "ellipsis",
                        wordBreak: "break-word",
                      }}
                    >
                      {item.SongName || item.AlbumName}
                    </Typography>
                  )}
                  {item.Artist && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: "var(--text-primary)", fontSize: "12px",
                        whiteSpace: "pre-line", textAlign: "center",
                        overflow: "hidden", textOverflow: "ellipsis",
                      }}
                    >
                      {item.Artist}
                    </Typography>
                  )}
                </Paper>
              ))}
              {items.length === 0 && (
                <Typography variant="body2" sx={{ color: "var(--text-faint)", alignSelf: "center", ml: 1 }}>
                  {t("tierList.empty")}
                </Typography>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default ReadOnlyTierList;
